import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from '../../components/buttons/Button';
import { useSocket } from '../../contexts/SocketContext';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { generateRandomString } from '../../helpers/common/generateRandomString';
import { useAlert } from '../../contexts/AlertContext';
import { Loader } from '../../components/loading/Loader';
import { CameraIcon, ExclamationCircleIcon, Cog8ToothIcon } from '@heroicons/react/24/outline';
import { getInfluxdbData } from "../../services/api/influxdb-tools";
import { CheckIcon, XMarkIcon } from "@heroicons/react/20/solid";
import { PolygonLassoOverlay } from "./PolygonLassoOverlay";

export const CalibrationPage = () => {
  const { deviceToken1, deviceToken2 } = useParams();
  const { showAlert } = useAlert();

  const { sendEvent, subscribeToEvent, unsubscribeFromEvent, socket } = useSocket();

  const [snapshots, setSnapshots] = useState([]);
  // const [snapshots, setSnapshots] = useState(['/9j/4A...//Z', '...']); // Debug purpose, example images as base64
  const [isUserReceivingSnapshot, setIsUserReceivingSnapshot] = useState(false);

  const [systemIsRunning, setSystemIsRunning] = useState(null);
  const [remainingBattery, setRemainingBattery] = useState(null);

  const [fullScreenSnapshot, setFullScreenSnapshot] = useState(null);
  const fullscreenAreaRef = useRef(null);
  const [imgOriginalSize, setImgOriginalSize] = useState([null, null]);

  const [lassoPoints, setLassoPoints] = useState([[], []]);
  const fullImgRef = useRef(null);
  const selectedImageId = useRef(null);

  const devicesNames = [process.env.REACT_APP_CUSTOMER_NAME + '_' + deviceToken1, process.env.REACT_APP_CUSTOMER_NAME + '_' + deviceToken2];

  // System is running or not
  useEffect(() => {
    const getData = async () => {
      const data = await getInfluxdbData(
        'engine',
        'bboxes',
        [['client_id', '==', devicesNames]],
        ['confidence'],
        '-5m',
        'now()',
        '1ms',
        null,
        'last',
        [],
        null,
        null,
        false,
        'desc',
      );
      setSystemIsRunning(data.length > 0);

      const batteryData = await getInfluxdbData(
        'engine',
        'battery_information',
        [['client_id', '==', devicesNames]],
        ['battery_percentage'],
        '-60m',
        'now()',
        '1ms',
        null,
        'last',
        [],
        null,
        null,
        false,
        'desc',
      );
      if (batteryData.length > 0)
        setRemainingBattery(Math.floor(batteryData[0].value));
    };

    getData();
  }, []);

  const handleSaveCalibration = async () => {
    if (snapshots.length !== 2) {
      showAlert('warning', 'You need to have two snapshots to save the calibration.');
      return;
    }

    const formData = new FormData();
    formData.append('deviceToken1', deviceToken1);
    formData.append('deviceToken2', deviceToken2);

    const filename1 = `snapshot1_${generateRandomString(5)}.jpg`;
    const filename2 = `snapshot2_${generateRandomString(5)}.jpg`;

    formData.append('file1', base64ToFile(snapshots[0], filename1));
    formData.append('file2', base64ToFile(snapshots[1], filename2));

    try {
      await axios.post(`${process.env.REACT_APP_API_ADDRESS}device_calibration_histories`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      showAlert('success', 'La calibration a été enregistrée.');
    } catch (error) {
      console.error('Error while saving calibration:', error);
      showAlert('error', "Erreur lors de l'enregistrement de la calibration.");
    }
  };

  const handleTakeSnapshot = () => {
    if (isUserReceivingSnapshot) return;
    setIsUserReceivingSnapshot(true);
    sendEvent('take_stream_snapshot', {deviceTokens: [deviceToken1, deviceToken2]});
    setIsUserReceivingSnapshot(true);
  };

  useEffect(() => {
    const handleStreamSnapshot = (data) => {
      setIsUserReceivingSnapshot(false);
      setSnapshots(data);
      updateOriginalSizeFromBase64(data);

      if (data !== undefined && data !== null && data.length === 0)
        showAlert('error', 'Impossible de récupérer les images des caméras.');
    };

    const handleReceivedData = (data) => {
      if (data?.working_zones) {
        setLassoPoints(
          [data.working_zones[deviceToken1].map((coords) => ({ x: coords[0], y: coords[1] })),
          data.working_zones[deviceToken2].map((coords) => ({ x: coords[0], y: coords[1] }))]
        );
      }
    };

    subscribeToEvent('stream_snapshot', handleStreamSnapshot);
    subscribeToEvent('current_data', handleReceivedData);

    return () => {
      unsubscribeFromEvent('stream_snapshot', handleStreamSnapshot);
      unsubscribeFromEvent('current_data', handleReceivedData);
    };
  }, [subscribeToEvent]);

  /* Polygon lasso management */
  // First, request the current working zones
  useEffect(() => {
    if (!socket) return;
    sendEvent('current_data', {request_working_zones: [deviceToken1, deviceToken2]});
  }, [socket]);

  const handleSelectImage = (imageId) => {
    setFullScreenSnapshot(snapshots[imageId]);
    selectedImageId.current = imageId;
  };

  const handleValidateZone = (e) => {
    e.stopPropagation();
    setFullScreenSnapshot(null);
    const zones = {
      [deviceToken1]: lassoPoints[0].map((coords) => [coords.x, coords.y]),
      [deviceToken2]: lassoPoints[1].map((coords) => [coords.x, coords.y]),
    };
    sendEvent('current_data', {update_working_zones: zones});
  };

  // To remove - Debug purpose
  // useEffect(() => {
  //   updateOriginalSizeFromBase64(snapshots);
  // }, []);

  const base64ToFile = (base64, filename) => {
    let byteString;
    let mimeString = '';

    if (base64.includes(',')) {
      byteString = atob(base64.split(',')[1]);
      mimeString = base64.split(',')[0].split(':')[1].split(';')[0];
    } else {
      byteString = atob(base64);
      mimeString = 'image/jpeg';
    }

    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }

    return new File([ab], filename, { type: mimeString });
  };

  const openSettings = () => {
    window.location = '/settings/devices/' + deviceToken1 + '/' + deviceToken2;
  };

  const imgFullScreenScale = useMemo(() => {
    const area = fullscreenAreaRef.current;
    if (imgOriginalSize[0] === null || imgOriginalSize[0] === 0 || !area) return [0, 0];
    return Math.min(area.clientWidth / imgOriginalSize[1], area.clientHeight / imgOriginalSize[0]);
  }, [imgOriginalSize, fullscreenAreaRef.current]);

  const updateOriginalSizeFromBase64 = (base64OrDataUrlList) => {
    if (base64OrDataUrlList[0] === null && base64OrDataUrlList[1] === null)
      return;

    const base64OrDataUrl = base64OrDataUrlList[0] !== null ? base64OrDataUrlList[0] : base64OrDataUrlList[1];

    return new Promise((resolve, reject) => {
      try {
        const file = base64ToFile(base64OrDataUrl, "image");
        const url = URL.createObjectURL(file);

        const img = new Image();

        img.onload = () => {
          setImgOriginalSize([img.naturalWidth, img.naturalHeight]);
          URL.revokeObjectURL(url); // cleanup
          resolve(true);
        };

        img.onerror = () => {
          URL.revokeObjectURL(url); // cleanup
          reject(new Error("Impossible de charger l'image (base64 invalide ?)"));
        };

        img.src = url;
      } catch (e) {
        reject(e);
      }
    });
  };

  if (systemIsRunning === null)
    return <Loader/>

  return (
    <div ref={fullscreenAreaRef} className='flex flex-col justify-center w-full h-full p-2 bg-white'>
      {
        systemIsRunning ?
          <>
            <div className="flex justify-between items-center gap-3 ml-2 mb-4 text-sm">
              {
                remainingBattery ?
                  <div className="flex items-center gap-3">
                    <span className='relative flex w-3 h-3'>
                      <span
                        className='absolute inline-flex w-full h-full rounded-full opacity-75 animate-ping bg-perception-success-600'
                      />
                      <span className='relative inline-flex w-3 h-3 rounded-full bg-perception-success-600' />
                    </span>
                    <span>Batterie : {remainingBattery}%</span>
                  </div>
                :
                  <div className="flex items-center gap-3">
                    <span className='relative flex w-3 h-3'>
                      <span className='relative inline-flex w-3 h-3 rounded-full bg-perception-success-600' />
                    </span>
                    <span>Système non connecté</span>
                  </div>
              }
              <button className="bg-perception-blue text-white px-4 py-2 rounded-md flex items-center" onClick={openSettings}>
                <Cog8ToothIcon className='size-5 mr-2' /> Paramétrage
              </button>
            </div>

            {/* Buttons */}
            <div className='flex items-center justify-center'>
              <div className='flex items-center gap-2'>
                {snapshots.length ? (
                  <Button type='button' category='tertiary-btn' size='small' onClick={handleSaveCalibration}>
                    Enregistrer
                  </Button>
                ) : (
                  ''
                )}
                <Button type='button' category='secondary-btn' size='small' onClick={handleTakeSnapshot}>
                  {snapshots.length ? 'Réessayer' : 'Prendre une capture'}
                </Button>
              </div>
            </div>

            {/* Loader */}
            {isUserReceivingSnapshot && <Loader />}

            {snapshots.length === 0 ? (
              /* Empty content */
              <div className='flex flex-col justify-center items-center h-full'>
                <CameraIcon className='w-32 h-32 text-gray-400' />
                <span className='text-lg text-gray-400 font-semibold'>Calibration des caméras</span>
              </div>
            ) : (
              /* Snapshots */
              <div className='flex flex-col justify-center items-stretch h-full gap-2 mt-2 camera-container'>
                {snapshots.length ?
                  <>
                    {snapshots[0] !== null ?
                      <div>
                        <img
                          src={`data:image/jpeg;base64,${snapshots[0]}`}
                          alt='Snapshot 1'
                          className='h-auto max-w-full rounded shadow w-full'
                          onClick={() => handleSelectImage(0)}
                        />
                      </div>
                      :
                      <div className='h-auto max-w-full bg-perception-gray-100 rounded shadow w-full flex flex-col items-center justify-center p-4 gap-2 text-perception-error-500'>
                        <ExclamationCircleIcon className='w-24 h-24 text-center' />
                        Impossible de récupérer l'image (Caméra #1)
                      </div>
                    }
                    {snapshots[1] !== null ?
                      <div>
                        <img
                          src={`data:image/jpeg;base64,${snapshots[1]}`}
                          alt='Snapshot 2'
                          className='h-auto max-w-full rounded shadow w-full'
                          onClick={() => handleSelectImage(1)}
                        />
                      </div>
                      :
                      <div className='h-auto max-w-full bg-perception-gray-100 rounded shadow w-full flex flex-col items-center justify-center p-4 gap-2 text-perception-error-500'>
                        <ExclamationCircleIcon className='w-24 h-24 text-center' />
                        Impossible de récupérer l'image (Caméra #2)
                      </div>
                    }
                  </>
                  : null
                }
              </div>
            )}
          </>
          :
          <div className='flex flex-col justify-center items-center'>
            <p className="text-center mb-4">Le système n'est pas encore actif.<br/>
            Merci de réessayer dans quelques minutes.</p>
            <Button type='button' category='tertiary-btn' size='small' onClick={() => window.location.reload()}>
              Recharger
            </Button>
          </div>
      }

      {fullScreenSnapshot &&
        <div className="fixed inset-0 z-[100] bg-white/70 backdrop-blur" onClick={() => setFullScreenSnapshot(null)}>
          <div className="absolute z-[150] bottom-6 right-6">
            <button type="button" className="bg-white text-perception-blue p-1 rounded-md size-8 flex items-center rotate-90 origin-center"
                    onClick={(e) => { e.stopPropagation(); setFullScreenSnapshot(null); }}>
              <XMarkIcon/>
            </button>

            <button type="button" className="bg-perception-blue text-white p-1 rounded-md size-8 flex items-center rotate-90 origin-center mt-2"
                    onClick={(e) => { handleValidateZone(e) }}>
              <CheckIcon/>
            </button>
          </div>

          <div className="w-full h-full relative" onClick={(e) => e.stopPropagation()}>
            <img
              ref={fullImgRef}
              src={`data:image/jpeg;base64,${fullScreenSnapshot}`}
              alt="Snapshot fullscreen"
              className={`block rounded-md select-none w-[${imgOriginalSize[0]}px] h-auto max-w-none absolute left-1/2 top-1/2`}
              draggable={false}
              onLoad={() => {
                updateOriginalSizeFromBase64(snapshots);
              }}
              style={{ transformOrigin: 'center', transform: `translate(-50%, -50%) rotate(90deg) scale(${imgFullScreenScale})` }}
            />

            <PolygonLassoOverlay
              imgRef={fullImgRef}
              points={lassoPoints[selectedImageId.current]}
              setPoints={(fn) =>
                setLassoPoints((prev) => {
                  const next = [...prev];
                  next[selectedImageId.current] = fn(prev[selectedImageId.current]);
                  return next;
                })
              }
              widthPx={imgOriginalSize[0]}
              heightPx={imgOriginalSize[1]}
              transform={`translate(-50%, -50%) rotate(90deg) scale(${imgFullScreenScale})`}
            />
          </div>
        </div>
      }
    </div>
  );
};
